/**
 * Tiny i18n layer. Locale resolution order:
 *   1. the signed-in user's `ui_locale` (synced from /api/auth/me)
 *   2. a per-browser override in localStorage
 *   3. navigator.language (el → Greek, else English)
 *
 * `t(key)` falls back to English, then to the key itself, so a missing
 * translation never blanks a label. Components call `useT()` so they
 * re-render when the locale flips.
 */
import { useSyncExternalStore } from "react"

export type Locale = "el" | "en"
const KEY = "lucy.web.locale"

const el: Record<string, string> = {
  // nav
  "nav.group.talk": "Συνομιλία", "nav.group.tools": "Εργαλεία", "nav.group.account": "Λογαριασμός",
  "nav.chat": "Συνομιλία",
  "nav.conversations": "Συζητήσεις",
  "nav.search": "Αναζήτηση",
  "auth.passkey": "Σύνδεση με passkey",
  "theme.title": "Εμφάνιση",
  "theme.sub": "Σκούρο, ανοιχτό, ή ό,τι έχει το σύστημα.",
  "theme.system": "Σύστημα",
  "theme.dark": "Σκούρο",
  "theme.light": "Ανοιχτό",
  "theme.hint": "Αλλάζει και από την παλέτα εντολών:",
  "palette.title": "Παλέτα εντολών",
  "palette.placeholder": "Πήγαινε σε… / ψάξε… / θέμα…",
  "palette.pages": "Σελίδες",
  "palette.recent": "Πρόσφατες συζητήσεις",
  "palette.theme": "Εμφάνιση",
  "palette.actions": "Ενέργειες",
  "palette.searchFor": "Αναζήτηση για",
  "palette.none": "Τίποτα δεν ταιριάζει.",
  "search.title": "Αναζήτηση",
  "search.sub": "Συζητήσεις, μνήμες, έγγραφα και email από ένα πεδίο, με παραπομπή στην πηγή.",
  "search.placeholder": "Τι ψάχνεις;",
  "search.go": "Ψάξε",
  "search.searching": "ψάχνω…",
  "search.hits": "αποτελέσματα",
  "search.none": "Τίποτα για αυτό. Δοκίμασε άλλη λέξη.",
  "search.conversations": "Συζητήσεις",
  "search.memories": "Μνήμες",
  "search.knowledge": "Έγγραφα",
  "search.mail": "Email",
  "search.citeConversation": "από συζήτηση · άνοιγμα στο chat",
  "search.citeMemory": "μνήμη",
  "search.citeKnowledge": "έγγραφο",
  "search.citeMail": "email",
  "nav.media": "Μέσα",
  "nav.projects": "Έργα",
  "nav.autonomy": "Αυτονομία",
  "nav.triggers": "Ενεργοποιητές",
  "nav.knowledge": "Γνώση",
  "nav.memory": "Μνήμη",
  "nav.brain": "Εγκέφαλος",
  "nav.schedules": "Προγράμματα",
  "nav.prompts": "Πρότυπα",
  "nav.connectors": "Συνδέσεις",
  "nav.account": "Λογαριασμός",
  "nav.billing": "Χρέωση",
  "nav.usage": "Χρήση",
  "nav.settings": "Ρυθμίσεις",
  "nav.operator": "Κονσόλα διαχειριστή",
  "nav.signout": "Αποσύνδεση",
  "nav.plan": "Πλάνο & χρήση",
  "nav.paused": "Σε παύση",
  "nav.openMenu": "Άνοιγμα μενού",
  "nav.closeMenu": "Κλείσιμο μενού",
  "nav.team": "Ομάδα",
  // team workspace
  "team.title": "Ομάδα",
  "team.sub": "Ένα κοινό workspace: κοινό μηνιαίο όριο, κοινή γνώση, θέσεις ανά μέλος.",
  "team.intro.title": "Μια Lucy για όλη την ομάδα",
  "team.intro.body": "Το πλάνο Team κοστίζει {price} € ανά θέση τον μήνα. Κάθε μέλος κρατά τον δικό του λογαριασμό και τη δική του μνήμη· η ομάδα μοιράζεται το μηνιαίο όριο (όριο × θέσεις) και όσες συλλογές γνώσης διαλέξετε.",
  "team.intro.soon": "Κοινές συνδέσεις (Gmail, Drive, Notion…): κάθε μέλος διαλέγει τι μοιράζεται.",
  "team.create": "Δημιουργία workspace",
  "team.createHint": "Χρειάζεσαι το πλάνο Team για να φτιάξεις workspace.",
  "team.upgrade": "Αναβάθμιση σε Team",
  "team.namePlaceholder": "Όνομα ομάδας",
  "team.rename": "Μετονομασία",
  "team.save": "Αποθήκευση",
  "team.cancel": "Άκυρο",
  "team.pool": "Κοινό όριο μήνα",
  "team.poolSeats": "{n} θέσεις × ${per}",
  "team.poolUnlimited": "Χωρίς όριο",
  "team.poolCredits": "+ ${credits} από προσκλήσεις",
  "team.seats": "θέσεις",
  "team.members": "Μέλη",
  "team.member.name": "Όνομα",
  "team.member.role": "Ρόλος",
  "team.member.spent": "Ξόδεψε (μήνας)",
  "team.member.joined": "Μπήκε",
  "team.role.owner": "Ιδιοκτήτης",
  "team.role.member": "Μέλος",
  "team.you": "εσύ",
  "team.remove": "Αφαίρεση",
  "team.removeConfirm": "Να αφαιρεθεί το μέλος {name} από την ομάδα;",
  "team.invites": "Εκκρεμείς προσκλήσεις",
  "team.invite.sent": "στάλθηκε {when}",
  "team.invite.revoke": "Ανάκληση",
  "team.invite.title": "Πρόσκληση μέλους",
  "team.invite.hint": "Θα λάβει email με σύνδεσμο που ισχύει 7 ημέρες, μόνο για αυτή τη διεύθυνση.",
  "team.invite.send": "Αποστολή πρόσκλησης",
  "team.invite.none": "Καμία εκκρεμής πρόσκληση.",
  "team.leave": "Αποχώρηση από την ομάδα",
  "team.leaveConfirm": "Να φύγεις από την ομάδα; Θα επιστρέψεις στο δικό σου πλάνο.",
  "team.delete": "Διαγραφή workspace",
  "team.deleteConfirm": "Να διαγραφεί το workspace «{name}»; Τα μέλη αποδεσμεύονται και η κοινή γνώση γίνεται ξανά ιδιωτική.",
  "team.danger": "Επικίνδυνη ζώνη",
  "team.knowledge": "Κοινή γνώση",
  "team.knowledgeSub": "Οι δικές σου συλλογές — διάλεξε ποιες βλέπει η ομάδα. Τα μέλη μπορούν να τις ψάχνουν, όχι να τις αλλάζουν.",
  "team.knowledge.shared": "Κοινή με την ομάδα",
  "team.knowledge.none": "Δεν έχεις συλλογές ακόμη.",
  "team.knowledge.others": "Από άλλα μέλη",
  "team.knowledge.by": "από {name}",
  "team.knowledge.readonly": "μόνο ανάγνωση",
  "team.knowledge.manage": "Διαχείριση συλλογών",
  "team.seatsLimit": "Όριο θέσεων",
  "team.seatsLimit.none": "χωρίς όριο",
  "team.seatsLimit.hint": "Κενό = χωρίς όριο· η χρέωση ακολουθεί τα μέλη.",
  "team.seatsLimit.used": "{used} σε χρήση",
  "team.seatsLimit.cost": "{seats} θέσεις × {price} € = {total} €/μήνα",
  "team.seatsLimit.saved": "Το όριο θέσεων αποθηκεύτηκε.",
  "team.conn.title": "Κοινά connectors",
  "team.conn.sub": "Συνδέσεις που μέλη μοιράζονται με την ομάδα. Όποιος δεν έχει δική του σύνδεση ίδιου τύπου χρησιμοποιεί την κοινή.",
  "team.conn.none": "Κανένα κοινό connector ακόμη — μοιράσου ένα από τη σελίδα Συνδέσεις.",
  "team.conn.manage": "Συνδέσεις",
  "team.conn.by": "από {name}",
  "team.conn.private": "Ιδιωτικό",
  "team.conn.read": "Ομάδα: ανάγνωση",
  "team.conn.full": "Ομάδα: πλήρες",
  "team.conn.readBadge": "μόνο ανάγνωση",
  "team.conn.fullBadge": "πλήρης πρόσβαση",
  "team.conn.fromTeam": "Από την ομάδα",
  "team.conn.fromTeamSub": "Συνδέσεις συναδέλφων που μπορείς να χρησιμοποιείς. Μόνο ο κάτοχος μπορεί να τις αποσυνδέσει.",
  "team.conn.shareLabel": "Κοινή χρήση με την ομάδα",
  "team.accept.title": "Πρόσκληση σε ομάδα",
  "team.accept.body": "{inviter} σε προσκαλεί στην ομάδα «{workspace}» στη Lucy.",
  "team.accept.emailHint": "Η πρόσκληση είναι για το {email}. Συνδέσου με αυτό το email.",
  "team.accept.signin": "Σύνδεση",
  "team.accept.signup": "Εγγραφή",
  "team.accept.accept": "Αποδοχή",
  "team.accept.expired": "Η πρόσκληση έχει λήξει. Ζήτησε καινούργια από τον ιδιοκτήτη της ομάδας.",
  "team.accept.used": "Η πρόσκληση έχει ήδη χρησιμοποιηθεί.",
  "team.accept.invalid": "Ο σύνδεσμος δεν είναι έγκυρος.",
  "team.accept.missing": "Λείπει το token. Άνοιξε τον σύνδεσμο από το email σου.",
  "team.accept.signedInAs": "Συνδεδεμένος ως {email}",
  "team.accept.goChat": "Πήγαινε στη Lucy",
  "team.accept.skip": "Όχι τώρα",
  // verify banner
  "verify.prompt": "Επιβεβαίωσε το email σου για να ξεκλειδώσεις τα πάντα.",
  "verify.sent": "Το email στάλθηκε. Αν δεν φτάσει σε ένα λεπτό, δες τα ανεπιθύμητα.",
  "verify.failed": "Η αποστολή απέτυχε: ",
  "verify.resend": "Αποστολή ξανά",
  "verify.sending": "Αποστολή…",
  // chat
  "chat.greeting.night": "Καληνύχτα",
  "chat.greeting.morning": "Καλημέρα",
  "chat.greeting.afternoon": "Καλησπέρα",
  "chat.greeting.evening": "Καλησπέρα",
  "chat.hero": "Πώς μπορώ να βοηθήσω{name};",
  "chat.heroSub": "Ρώτησέ με οτιδήποτε, γράψε ένα μήνυμα, σχεδίασε τη μέρα σου, ή συνέχισε από εκεί που μείναμε.",
  "chat.chip.plan": "Σχεδίασε τη μέρα μου",
  "chat.chip.email": "Γράψε μου ένα email",
  "chat.chip.recap": "Τι συζητήσαμε τελευταία;",
  "chat.chip.image": "Φτιάξε μου μια εικόνα",
  "chat.placeholder.new": "Μήνυμα στη Lucy…",
  "chat.placeholder.continue": "Συνέχισε τη συζήτηση…",
  "chat.queued": "στη σειρά…",
  "chat.thinking": "σκέφτομαι…",
  "chat.disclaimer": "Η Lucy μπορεί να κάνει λάθη. Έλεγξε σημαντικές πληροφορίες.",
  "chat.new": "Νέα",
  "chat.newConversation": "Νέα συζήτηση",
  "chat.untitled": "Χωρίς τίτλο",
  "chat.search": "Αναζήτηση συζητήσεων…",
  "chat.retry": "Ξαναπροσπάθησε",
  "chat.stop": "Διακοπή",
  "chat.send": "Αποστολή",
  "chat.attach": "Επισύναψη αρχείου ή εικόνας",
  "att.preview": "Προβολή",
  "att.hide": "Κλείσιμο",
  "att.download": "Λήψη",
  "att.loading": "φορτώνει…",
  "att.failed": "Δεν φορτώθηκε",
  "att.docs": "Έγγραφα",
  "att.docsTitle": "Έγγραφα της συζήτησης",
  "att.docsEmpty": "Δεν υπάρχουν έγγραφα σε αυτή τη συζήτηση.",
  "chat.uploading": "ανέβασμα…",
  // auth
  "auth.signinTitle": "Σύνδεση στη Lucy",
  "auth.welcomeBack": "Καλώς ήρθες ξανά.",
  "auth.email": "Email",
  "auth.password": "Κωδικός",
  "auth.totp": "Κωδικός 2FA",
  "auth.signin": "Σύνδεση",
  "auth.forgot": "Ξέχασες τον κωδικό;",
  "auth.createAccount": "Δημιουργία λογαριασμού",
  "auth.signupTitle": "Δημιούργησε τον λογαριασμό σου στη Lucy",
  "auth.inviteOnly": "Η Lucy είναι μόνο με πρόσκληση αυτή τη στιγμή. Βάλε τον κωδικό που σου δόθηκε.",
  "auth.freeToStart": "Δωρεάν για αρχή. Αναβάθμιση όποτε θέλεις.",
  "auth.inviteCode": "Κωδικός πρόσκλησης",
  "auth.displayName": "Όνομα εμφάνισης",
  "auth.optional": "(προαιρετικό)",
  "auth.min12": "Τουλάχιστον 12 χαρακτήρες.",
  "auth.haveAccount": "Έχεις ήδη λογαριασμό;",
  "auth.inviteRequired": "Χρειάζεται κωδικός πρόσκλησης.",
  "auth.pwTooShort": "Ο κωδικός πρέπει να έχει τουλάχιστον 12 χαρακτήρες.",
  "auth.acceptTerms": "Συμφωνώ με τους {terms} και την {privacy}.",
  // referrals
  "ref.title": "Πρόσκληση φίλων",
  "ref.sub": "Μοιράσου τον σύνδεσμό σου. Όταν ο φίλος σου επιβεβαιώσει το email του, παίρνετε και οι δύο ${amount} επιπλέον μηνιαίο όριο για {days} ημέρες.",
  "ref.link": "Ο σύνδεσμός σου",
  "ref.copy": "Αντιγραφή",
  "ref.copied": "Αντιγράφηκε",
  "ref.share": "Κοινοποίηση",
  "ref.shareText": "Δοκίμασε τη Lucy — με τον σύνδεσμό μου παίρνουμε και οι δύο ${amount} επιπλέον όριο.",
  "ref.earned": "Κέρδισες",
  "ref.available": "Διαθέσιμο τώρα",
  "ref.referredCount": "Προσκεκλημένοι",
  "ref.none": "Κανείς ακόμα. Στείλε τον σύνδεσμο σε έναν φίλο.",
  "ref.verified": "επιβεβαιωμένο",
  "ref.pending": "εκκρεμεί",
  "ref.disabled": "Οι προσκλήσεις φίλων είναι προσωρινά απενεργοποιημένες.",
  "ref.loadFailed": "Δεν φορτώθηκαν τα στοιχεία πρόσκλησης.",
  "ref.invitedBy": "Σε προσκάλεσε {name} · θα πάρετε και οι δύο ${amount}",
  "ref.invalidLink": "Αυτός ο σύνδεσμος πρόσκλησης δεν ισχύει.",
  "auth.terms": "Όρους χρήσης",
  "auth.privacy": "Πολιτική απορρήτου",
  "auth.mustAccept": "Πρέπει να αποδεχτείς τους Όρους χρήσης και την Πολιτική απορρήτου.",
  "legal.terms": "Όροι χρήσης",
  "legal.privacy": "Πολιτική απορρήτου",
  "meter.month": "αυτόν τον μήνα",
  "meter.unlimited": "χωρίς όριο",
  "meter.warn": "Πλησιάζεις το μηνιαίο όριο.",
  "notif.title": "Ειδοποιήσεις",
  "notif.empty": "Τίποτα νέο.",
  "notif.card": "Ειδοποιήσεις",
  "notif.cardSub": "Πού σε βρίσκει η Lucy για υπενθυμίσεις, briefs και εγκρίσεις.",
  "notif.browser": "Ειδοποιήσεις στον browser",
  "notif.browserOn": "Ενεργές σε αυτή τη συσκευή",
  "notif.browserOff": "Ανενεργές σε αυτή τη συσκευή",
  "notif.enable": "Ενεργοποίηση",
  "notif.disable": "Απενεργοποίηση",
  "notif.unsupported": "Ο browser δεν υποστηρίζει push.",
  "push.nudge": "Θέλεις ειδοποιήσεις από τη Lucy σε αυτή τη συσκευή; Υπενθυμίσεις και απαντήσεις, ακόμα και με κλειστή την εφαρμογή.",
  "push.enable": "Ενεργοποίηση", "push.later": "Αργότερα",
  "notif.telegram": "Telegram",
  "notif.push": "Push στο κινητό (iOS)",
  "notif.webpush": "Push στον browser",
  "notif.email": "Email όταν δεν υπάρχει άλλο κανάλι",
  "notif.digest": "Εβδομαδιαίο email με περίληψη (Δευτέρα 08:00)",
  "notif.whatsapp": "WhatsApp (όταν ο connector έχει ενεργές ειδοποιήσεις)",
  "notif.hourly": "Ωριαίος έλεγχος: inbox, ημερολόγιο, εκκρεμότητες. Σε ειδοποιεί μόνο για ό,τι θέλει πράξη (08:00–22:00)",
  "fb.up": "Καλή απάντηση",
  "fb.down": "Κάτι δεν πήγε καλά",
  "fb.why": "Τι έφταιγε; (προαιρετικό)",
  "offline.title": "Χωρίς σύνδεση",
  "offline.ws": "Επανασύνδεση…",
  "tts.listen": "Ακρόαση",
  "tts.stop": "Στοπ",
  "voice.on": "Φωνητική συνομιλία",
  "voice.off": "Τέλος φωνητικής συνομιλίας",
  "voice.listening": "ακούω…",
  "voice.hearing": "μιλάς…",
  "voice.transcribing": "απομαγνητοφώνηση…",
  "voice.speaking": "μιλάει η Lucy…",
  "voice.thinking": "σκέφτεται…",
  "chat.here": "εδώ είμαι",
  "media.editTitle": "Επεξεργασία με οδηγίες",
  "media.editHint": "π.χ. «άλλαξε το φόντο σε παραλία», «βγάλε τα γυαλιά»",
  "media.editNote": "Η αρχική μένει· βγαίνει νέα έκδοση δίπλα της.",
  "media.editGo": "Φτιάξ' το",
  "media.versions": "Εκδόσεις",
  "media.original": "Αρχική",
  "media.share": "Κοινή χρήση με link",
  "media.shareTitle": "Κοινή χρήση άλμπουμ",
  "media.shareNote": "Όποιος έχει το link βλέπει τις φωτογραφίες του άλμπουμ μέχρι να λήξει. Οι κρυφές δεν βγαίνουν ποτέ.",
  "media.share1d": "Για 1 μέρα",
  "media.share7d": "Για 7 μέρες",
  "media.share30d": "Για 30 μέρες",
  "media.shareMake": "Φτιάξε link",
  "media.shareExpires": "Λήγει:",
  "media.shareCopy": "Αντιγραφή",
  "media.shareCopied": "Αντιγράφηκε",
  "brain.title": "Σημειωματάριο",
  "brain.sub": "Ό,τι ξέρει η Lucy για σένα, σε λέξεις. Διάβασέ το, διόρθωσέ το, σβήσε ό,τι δεν ισχύει.",
  "brain.search": "Ψάξε στη μνήμη…",
  "brain.all": "Όλα",
  "brain.total": "Σύνολο",
  "brain.memories": "μνήμες",
  "brain.loading": "φορτώνω τη μνήμη…",
  "brain.emptyTitle": "Τίποτα ακόμα",
  "brain.emptyHint": "Μίλα λίγο με τη Lucy και το σημειωματάριο θα γεμίσει με όσα μαθαίνει για σένα.",
  "brain.recent": "Πρόσφατα",
  "brain.important": "Σημαντικά",
  "brain.noMatch": "Δεν βρέθηκε κάτι.",
  "brain.correct": "Διόρθωσε",
  "brain.forget": "Ξέχασέ το",
  "brain.forgetConfirm": "Να το ξεχάσει η Lucy οριστικά;",
  "brain.save": "Αποθήκευση",
  "brain.cancel": "Άκυρο",
  "brain.more": "… και {n} ακόμα. Περιόρισε με αναζήτηση ή θέμα.",
  "brain.why": "γιατί το θυμάμαι",
  "brain.fromConversation": "από αυτή τη συζήτηση",
  "voice.muted": "σίγαση",
  "voice.you": "Εσύ",
  "voice.mute": "Σίγαση μικροφώνου",
  "voice.unmute": "Άνοιγμα μικροφώνου",
  "voice.end": "Τέλος",
  "voice.keyboard": "Γύρνα σε κείμενο",
  // video call
  "call.title": "Βιντεοκλήση με τη Lucy",
  "call.start": "Βιντεοκλήση",
  "call.starting": "ξεκινάει η κλήση…",
  "call.connecting": "σύνδεση…",
  "call.noVideoYet": "Η Lucy έρχεται…",
  "call.minutesLeft": "{n} λεπτά ακόμη",
  "call.unlimited": "χωρίς όριο",
  "call.end": "Τέλος κλήσης",
  "call.close": "Κλείσιμο",
  "call.tapForSound": "Πάτα για ήχο",
  "call.micDenied": "Δεν έχω πρόσβαση στο μικρόφωνο. Επίτρεψέ το στις ρυθμίσεις του browser και ξαναδοκίμασε.",
  "call.connLost": "Χάθηκε η σύνδεση με το βίντεο.",
  "call.failed": "Η κλήση δεν ξεκίνησε: {msg}",
  "call.turnFailed": "Δεν πήρα απάντηση: {msg}",
  "call.capReached": "Τελείωσαν τα λεπτά βιντεοκλήσης για αυτόν τον μήνα. Ανανεώνονται την 1η.",
  "call.endedError": "Η κλήση τερματίστηκε.",
  "conv.title": "Συζητήσεις",
  "conv.subtitle": "Ό,τι έχεις συζητήσει με τη Lucy. Πλήρης αναζήτηση.",
  "conv.search": "Αναζήτηση σε τίτλο ή κείμενο",
  "conv.showArchived": "Αρχειοθετημένες",
  "conv.rename": "Μετονομασία",
  "conv.pin": "Καρφίτσωμα",
  "conv.unpin": "Ξεκαρφίτσωμα",
  "conv.archive": "Αρχειοθέτηση",
  "conv.unarchive": "Επαναφορά",
  "conv.delete": "Διαγραφή",
  "conv.empty": "Δεν υπάρχουν συζητήσεις",
  "conv.emptyHint": "Πήγαινε στη Συνομιλία για να ξεκινήσεις.",
  "conv.messages": "μηνύματα",
  "conv.lastActive": "τελευταία δραστηριότητα",
  "slash.hint": "Πρότυπα: πληκτρολόγησε / και ένα όνομα",
  // settings / language
  "lang.title": "Γλώσσα",
  "lang.subtitle": "Γλώσσα της εφαρμογής και γλώσσα στην οποία απαντά η Lucy.",
  "lang.ui": "Γλώσσα εφαρμογής",
  "lang.reply": "Η Lucy απαντά σε",
  "lang.mirror": "Ό,τι γράφω εγώ",
  "lang.el": "Ελληνικά",
  "lang.en": "Αγγλικά",
  "lang.saved": "Αποθηκεύτηκε.",
  // account
  "account.profile": "Προφίλ",
  "account.displayName": "Όνομα εμφάνισης",
  "account.timezone": "Ζώνη ώρας",
  "account.save": "Αποθήκευση",
  "account.edit": "Επεξεργασία",
  "account.cancel": "Άκυρο",
  // onboarding
  "welcome.title": "Καλώς ήρθες στη Lucy",
  "welcome.subtitle": "Τρία γρήγορα βήματα και ξεκινάμε.",
  "welcome.step1": "Για σένα",
  "welcome.step2": "Συνδέσεις",
  "welcome.step3": "Ξεκίνα",
  "welcome.nameLabel": "Πώς να σε λέω;",
  "welcome.namePh": "Το όνομά σου",
  "welcome.next": "Συνέχεια",
  "welcome.back": "Πίσω",
  "welcome.skip": "Παράλειψη",
  "welcome.connectIntro": "Σύνδεσε ό,τι χρησιμοποιείς καθημερινά. Μπορείς να το κάνεις και αργότερα από τις Συνδέσεις.",
  "welcome.google": "Google (Gmail, Ημερολόγιο, Drive)",
  "welcome.googleHint": "Για να διαβάζει email, να βάζει ραντεβού και να βρίσκει αρχεία.",
  "welcome.telegram": "Telegram",
  "welcome.telegramHint": "Για υπενθυμίσεις και συνομιλία από το κινητό σου.",
  "welcome.connect": "Σύνδεση",
  "welcome.connected": "Συνδεδεμένο",
  "welcome.readyTitle": "Έτοιμοι.",
  "welcome.readyBody": "Δοκίμασε ένα από αυτά, ή γράψε ό,τι θέλεις.",
  "welcome.start": "Πάμε στη συνομιλία",
  // schedules
  "sched.title": "Προγράμματα",
  "sched.subtitle": "Πράγματα που η Lucy κάνει μόνη της, σε τακτά διαστήματα.",
  "sched.new": "Νέο πρόγραμμα",
  "sched.empty": "Δεν υπάρχουν προγράμματα ακόμη",
  "sched.emptyHint": "Π.χ. κάθε πρωί στις 8 μια περίληψη των ειδήσεων για το θέμα Χ.",
  "sched.name": "Όνομα",
  "sched.what": "Τι να κάνει η Lucy;",
  "sched.kind.reminder": "Να μου στέλνει ένα μήνυμα",
  "sched.kind.research_brief": "Να ερευνά ένα θέμα και να μου στέλνει περίληψη",
  "sched.kind.agent_run": "Να εκτελεί μια εργασία αυτόνομα",
  "sched.reminderText": "Το μήνυμα",
  "sched.query": "Θέμα έρευνας",
  "sched.goal": "Η οδηγία προς τη Lucy",
  "sched.when": "Πότε;",
  "sched.at": "στις",
  "sched.custom": "Προσαρμοσμένο cron",
  "sched.cadence.daily": "Κάθε μέρα",
  "sched.cadence.weekdays": "Καθημερινές",
  "sched.cadence.weekly": "Κάθε εβδομάδα",
  "sched.cadence.hourly": "Κάθε ώρα",
  "sched.cadence.every_4_hours": "Κάθε 4 ώρες",
  "sched.cadence.monthly": "Κάθε μήνα (1η)",
  "sched.cadence.custom": "Προσαρμοσμένο",
  "sched.timezone": "Ζώνη ώρας",
  "sched.create": "Δημιουργία",
  "sched.cancel": "Άκυρο",
  "sched.delete": "Διαγραφή",
  "sched.next": "Επόμενο",
  "sched.lastError": "Τελευταίο σφάλμα",
  "sched.followup": "Παρακολούθηση",
  "sched.followupOn": "Να ελέγξει μετά και να με ειδοποιήσει μόνο αν εκκρεμεί",
  "sched.followupHours": "ώρες μετά από κάθε εκτέλεση",
  "sched.followupCheckHint": "Τι να ελέγξει; π.χ. «αν ο Γιώργος απάντησε στην αναφορά»",
  "sched.followupNote": "Η Lucy ψάχνει πραγματικά στοιχεία (απάντηση στο thread, ραντεβού, ολοκληρωμένη εργασία). Αν όλα καλά, σιωπά.",
  "sched.followupBadge": "έλεγχος σε {h} ώρες",
  "day.1": "Δευτέρα", "day.2": "Τρίτη", "day.3": "Τετάρτη", "day.4": "Πέμπτη",
  "day.5": "Παρασκευή", "day.6": "Σάββατο", "day.0": "Κυριακή",
  // media (routes/_app.media.tsx)
  "media.title": "Μέσα",
  "media.sub": "Κάθε εικόνα που έφτιαξε η Lucy για σένα, συν ό,τι προσθέτεις εσύ.",
  "media.inLibrary": "{n} στη βιβλιοθήκη",
  "media.hiddenCount": "{n} κρυφές",
  "media.add": "Προσθήκη",
  "media.lockVault": "Κλείδωμα",
  "media.all": "Όλες",
  "media.favorites": "Αγαπημένες",
  "media.videos": "Βίντεο",
  "media.video": "βίντεο",
  "media.hidden": "Κρυφές",
  "media.done": "Τέλος",
  "media.select": "Επιλογή",
  "media.deselect": "Αποεπιλογή",
  "media.album": "Άλμπουμ",
  "media.searchPrompts": "Αναζήτηση σε prompts…",
  "media.deleteAlbum": "διαγραφή άλμπουμ",
  "media.deleteAlbumConfirm": "Διαγραφή του άλμπουμ «{name}»; Οι εικόνες μένουν στη βιβλιοθήκη.",
  "media.loading": "Φόρτωση…",
  "media.emptyHidden": "Τίποτα κρυφό. Άνοιξε μια εικόνα και διάλεξε «Απόκρυψη» για να τη φέρεις εδώ.",
  "media.emptySearch": "Καμία εικόνα δεν ταιριάζει στην αναζήτηση.",
  "media.emptyBefore": "Τίποτα εδώ ακόμη. Ζήτα από τη Lucy στη",
  "media.emptyLink": "συνομιλία",
  "media.emptyAfter": "να ζωγραφίσει κάτι, ή πρόσθεσε δικές σου.",
  "media.showingLatest": "Βλέπεις τις {n} πιο πρόσφατες από {total}. Ψάξε για να περιορίσεις.",
  "media.selected": "{n} επιλεγμένες",
  "media.selectAll": "Επιλογή όλων ({n})",
  "media.clearSelection": "Καθαρισμός επιλογής",
  "media.downloadZip": "Λήψη .zip",
  "media.hide": "Απόκρυψη",
  "media.unhide": "Εμφάνιση",
  "media.unlockToUnhide": "Ξεκλείδωσε για εμφάνιση",
  "media.favourite": "Αγαπημένο",
  "media.favorite": "Αγαπημένο",
  "media.favorited": "Στα αγαπημένα",
  "media.unfavorite": "Αφαίρεση από τα αγαπημένα",
  "media.delete": "Διαγραφή",
  "media.deleteOneConfirm": "Διαγραφή 1 εικόνας;",
  "media.deleteManyConfirm": "Διαγραφή {n} εικόνων;",
  "media.deleteForGood": "Να διαγραφεί οριστικά αυτή η εικόνα;",
  "media.download": "Λήψη",
  "media.close": "Κλείσιμο",
  "media.previous": "Προηγούμενη",
  "media.next": "Επόμενη",
  "media.badgeHidden": "κρυφή",
  "media.created": "Δημιουργήθηκε",
  "media.size": "Μέγεθος",
  "media.provider": "Πάροχος",
  "media.albumNone": "— κανένα —",
  "media.openConversation": "Άνοιγμα της συζήτησης",
  "media.newAlbum": "Νέο άλμπουμ",
  "media.albumPlaceholder": "π.χ. Πορτρέτα της Lucy",
  "media.cancel": "Άκυρο",
  "media.create": "Δημιουργία",
  "media.unlockTitle": "Ξεκλείδωμα κρυφών εικόνων",
  "media.unlockHint": "Βάλε τον κωδικό του λογαριασμού σου. Το θησαυροφυλάκιο μένει ανοιχτό για 15 λεπτά σε αυτή την καρτέλα και μετά κλειδώνει μόνο του.",
  "media.password": "Κωδικός",
  "media.unlock": "Ξεκλείδωμα",
  "media.wrongPassword": "Δεν είναι ο κωδικός σου.",
  "media.toast.deleted": "Διαγράφηκε",
  "media.toast.added": "Προστέθηκε στη βιβλιοθήκη σου",
  "media.toast.backToLibrary": "Επέστρεψε στη βιβλιοθήκη",
  "media.toast.movedHidden": "Μεταφέρθηκε στα Κρυφά",
  "media.toast.zipOne": "Λήψη 1 εικόνας ως .zip",
  "media.toast.zipMany": "Λήψη {n} εικόνων ως .zip",
  "media.bulk.deleted": "{n} διαγράφηκαν",
  "media.bulk.hidden": "{n} κρύφτηκαν",
  "media.bulk.unhidden": "{n} εμφανίστηκαν ξανά",
  "media.bulk.favourited": "{n} στα αγαπημένα",
  "media.bulk.updated": "{n} ενημερώθηκαν",
  "media.bulk.failed": ", {n} απέτυχαν",
  // autonomy (routes/_app.autonomy.tsx)
  "auto.title": "Αυτονομία",
  "auto.subBefore": "Τι κάνει η Lucy μόνη της και πού περιμένει εσένα. Ξεκίνα μια εκτέλεση από ένα",
  "auto.subLink": "έργο",
  "auto.subAfter": "ή ζητώντας το στη συνομιλία.",
  "auto.refresh": "Ανανέωση",
  "auto.needsApproval": "Χρειάζεται την έγκρισή σου",
  "auto.loading": "Φόρτωση…",
  "auto.noApprovals": "Τίποτα σε αναμονή. Οι ριψοκίνδυνες ενέργειες (αποστολή, push, διαγραφή) θα εμφανίζονται εδώ — και στο Telegram — πριν τις κάνει η Lucy.",
  "auto.runs": "Εκτελέσεις",
  "auto.filter.active": "ενεργές",
  "auto.filter.all": "όλες",
  "auto.filter.done": "ολοκληρωμένες",
  "auto.filter.failed": "αποτυχημένες",
  "auto.noActiveRuns": "Καμία εκτέλεση σε εξέλιξη.",
  "auto.noRuns": "Καμία εκτέλεση εδώ ακόμη.",
  "auto.toast.asking": "Η Lucy ζητά την έγκρισή σου",
  "auto.toast.approved": "Εγκρίθηκε — η Lucy συνεχίζει",
  "auto.toast.denied": "Απορρίφθηκε — η Lucy θα προσαρμοστεί",
  "auto.toast.cancelled": "Η εκτέλεση ακυρώθηκε",
  "auto.expired": "έληξε",
  "auto.leftHM": "απομένουν {h}ω {m}λ",
  "auto.leftM": "απομένουν {m}λ",
  "auto.risk.read": "ανάγνωση",
  "auto.risk.local_write": "τοπική εγγραφή",
  "auto.risk.external_write": "εξωτερική εγγραφή",
  "auto.risk.destructive": "καταστροφική",
  "auto.risk.credential": "διαπιστευτήρια",
  "auto.showPayload": "Εμφάνιση ακριβούς φορτίου",
  "auto.hidePayload": "Απόκρυψη ακριβούς φορτίου",
  "auto.openRun": "Άνοιγμα εκτέλεσης",
  "auto.approve": "Έγκριση",
  "auto.deny": "Απόρριψη",
  "auto.status.pending": "στην ουρά",
  "auto.status.running": "τρέχει",
  "auto.status.waiting_approval": "περιμένει εσένα",
  "auto.status.waiting_local_agent": "περιμένει το Mac σου",
  "auto.status.done": "ολοκληρώθηκε",
  "auto.status.failed": "απέτυχε",
  "auto.status.cancelled": "ακυρώθηκε",
  "auto.turns": "{used} / {max} γύροι",
  "auto.cancel": "Ακύρωση",
  "auto.spentOf": "${spent} από ${budget}",
  "auto.started": "ξεκίνησε {when}",
  "auto.finished": "τελείωσε {when}",
  "auto.close": "Κλείσιμο",
  "auto.report": "Αναφορά",
  "auto.noSteps": "Δεν έχουν καταγραφεί βήματα ακόμη.",
  "auto.step.error": "σφάλμα",
  "auto.step.denied": "απορρίφθηκε",
  "auto.step.skipped": "παραλείφθηκε",
  // triggers (routes/_app.triggers.tsx)
  "trig.title": "Ενεργοποιητές",
  "trig.subBefore": "Μόνιμοι κανόνες — όταν συμβεί κάτι, η Lucy ενεργεί χωρίς να της το ζητήσεις. Οι εκτελέσεις εμφανίζονται στην",
  "trig.subLink": "Αυτονομία",
  "trig.subAfter": "· ό,τι είναι ριψοκίνδυνο περιμένει πάντα την έγκρισή σου.",
  "trig.new": "Νέος ενεργοποιητής",
  "trig.recipes": "Συνταγές",
  "trig.loading": "Φόρτωση…",
  "trig.empty": "Δεν υπάρχουν ενεργοποιητές ακόμη. Η Lucy ενεργεί μόνο όταν της το ζητάς, ή βάσει προγράμματος.",
  "trig.createFirst": "Φτιάξε τον πρώτο σου ενεργοποιητή",
  "trig.deleteConfirm": "Διαγραφή του «{name}»;",
  "trig.untitled": "Ενεργοποιητής χωρίς όνομα",
  "trig.toast.created": "Ο ενεργοποιητής δημιουργήθηκε",
  "trig.toast.deleted": "Ο ενεργοποιητής διαγράφηκε",
  "trig.test.job": "Η δοκιμή έγινε — ξεκίνησε εκτέλεση, δες την Αυτονομία",
  "trig.test.approval": "Η δοκιμή έγινε — μια έγκριση σε περιμένει",
  "trig.test.notified": "Η δοκιμή έγινε — δες το Telegram",
  "trig.test.skipped": "Η δοκιμή παραλείφθηκε: {reason}",
  "trig.test.noAction": "καμία ενέργεια",
  "trig.source.gmail": "Έρχεται email",
  "trig.source.gmail.blurb": "Παρακολουθεί το Gmail σου με μια αναζήτηση. Έλεγχος κάθε 2 λεπτά.",
  "trig.source.gcalendar": "Αλλάζει το ημερολόγιο",
  "trig.source.gcalendar.blurb": "Ενεργοποιείται όταν ένα συμβάν δημιουργείται, μετακινείται ή ακυρώνεται. Έλεγχος κάθε 2 λεπτά.",
  "trig.source.github": "Δραστηριότητα στο GitHub",
  "trig.source.github.blurb": "Pushes, PRs, issues, αποτυχημένο CI. Επικολλάς ένα webhook URL στις ρυθμίσεις του repo.",
  "trig.source.webhook": "Οτιδήποτε άλλο",
  "trig.source.webhook.blurb": "Ένα URL + secret όπου μπορεί να κάνει POST οποιαδήποτε υπηρεσία (Zapier, IFTTT, ένα cron στον server σου…).",
  "trig.disable": "Απενεργοποίηση ενεργοποιητή",
  "trig.enable": "Ενεργοποίηση ενεργοποιητή",
  "trig.mode.job": "ενεργεί",
  "trig.mode.proposal": "πρόταση",
  "trig.mode.notify": "ειδοποίηση",
  "trig.modeHelp.job": "Η Lucy ξεκινά αμέσως (τα ριψοκίνδυνα βήματα σε ρωτούν πρώτα).",
  "trig.modeHelp.proposal": "Η Lucy σε ρωτάει πρώτα και το κάνει όταν εγκρίνεις.",
  "trig.modeHelp.notify": "Η Lucy απλώς σου λέει ότι συνέβη.",
  "trig.modeBtn.notify": "με ενημερώνει",
  "trig.modeBtn.proposal": "ρωτάει πρώτα",
  "trig.modeBtn.job": "ενεργεί",
  "trig.fired": "ενεργοποιήθηκε {n}×",
  "trig.neverFired": "δεν έχει ενεργοποιηθεί ποτέ",
  "trig.last": "τελευταία {when}",
  "trig.test": "Δοκιμή",
  "trig.delete": "Διαγραφή",
  "trig.filter.search": "αναζήτηση: {v}",
  "trig.filter.from": "από: {v}",
  "trig.filter.mentions": "αναφέρει: {v}",
  "trig.filter.allKinds": "δημιουργία / ενημέρωση / ακύρωση",
  "trig.filter.failuresOnly": "μόνο αποτυχίες",
  "trig.webhook.github": "Επικόλλησέ το στο GitHub → Settings → Webhooks",
  "trig.webhook.post": "Κάνε POST εδώ με το secret header",
  "trig.when": "Όταν…",
  "trig.gmailSearch": "Αναζήτηση Gmail",
  "trig.ph.from": "Μόνο από… (email ή όνομα, χωρισμένα με κόμμα, προαιρετικό)",
  "trig.ph.mentions": "…και αναφέρει (χωρισμένα με κόμμα, προαιρετικό)",
  "trig.ph.calKeywords": "Μόνο συμβάντα που αναφέρουν… (χωρισμένα με κόμμα, προαιρετικό)",
  "trig.onlyFailingCI": "Μόνο αποτυχημένο CI",
  "trig.then": "Τότε η Lucy…",
  "trig.ph.gmailInstruction": "Γράψε μια απάντηση και συνόψισε τι θέλουν.",
  "trig.ph.instruction": "Τι να κάνει η Lucy όταν ενεργοποιηθεί;",
  "trig.ph.name": "Δώσε του όνομα (π.χ. «Email από το αφεντικό»)",
  "trig.cancel": "Άκυρο",
  "trig.create": "Δημιουργία",
  "trig.recipe.meeting-prep.title": "Προετοίμασέ με για νέες συναντήσεις",
  "trig.recipe.meeting-prep.desc": "Όταν μπαίνει μια συνάντηση στο ημερολόγιο, η Lucy ερευνά ποιοι έρχονται και ετοιμάζει σημεία συζήτησης.",
  "trig.recipe.meeting-prep.name": "Προετοιμασία συναντήσεων",
  "trig.recipe.meeting-cancelled.title": "Πες μου όταν ακυρώνεται μια συνάντηση",
  "trig.recipe.meeting-cancelled.desc": "Μια γρήγορη ειδοποίηση για να μην πάει χαμένη η ώρα.",
  "trig.recipe.meeting-cancelled.name": "Ακυρωμένες συναντήσεις",
  "trig.recipe.vip-email.title": "Σημείωσε email από κάποιον σημαντικό",
  "trig.recipe.vip-email.desc": "Όρισε τον αποστολέα· η Lucy συνοψίζει τι θέλει και ετοιμάζει απάντηση για να την εγκρίνεις.",
  "trig.recipe.vip-email.name": "Σημαντικός αποστολέας",
  "trig.recipe.invoices.title": "Πιάσε τιμολόγια και προθεσμίες",
  "trig.recipe.invoices.desc": "Τιμολόγια και αποδείξεις εξάγονται και γίνονται υπενθύμιση πριν την προθεσμία.",
  "trig.recipe.invoices.name": "Τιμολόγια",
  "trig.recipe.travel.title": "Βάλε τις κρατήσεις ταξιδιών στο ημερολόγιο",
  "trig.recipe.travel.desc": "Επιβεβαιώσεις πτήσεων, ξενοδοχείων και τρένων γίνονται συμβάντα ημερολογίου.",
  "trig.recipe.travel.name": "Κρατήσεις ταξιδιών",
  "trig.recipe.ci-failures.title": "Ερεύνησε αποτυχίες CI",
  "trig.recipe.ci-failures.desc": "Όταν αποτυγχάνει ένα workflow, η Lucy διαβάζει το log και προτείνει διόρθωση.",
  "trig.recipe.ci-failures.name": "Αποτυχίες CI",
  "trig.recipe.new-issues.title": "Ταξινόμησε νέα issues",
  "trig.recipe.new-issues.desc": "Κάθε νέο issue παίρνει πρόταση label, εκτίμηση σοβαρότητας και πρόχειρη πρώτη απάντηση.",
  "trig.recipe.new-issues.name": "Ταξινόμηση issues",
  // knowledge (routes/_app.knowledge.tsx)
  "know.title": "Γνώση",
  "know.sub": "Ευρετηρίασε τα έγγραφά σου για να τα παραθέτει η Lucy στη συνομιλία.",
  "know.new": "Νέα συλλογή",
  "know.empty": "Δεν υπάρχουν συλλογές ακόμη",
  "know.emptyHint": "Ανέβασε PDF, Word, PowerPoint, Excel, κείμενο ή αρχεία κώδικα — ή σύνδεσε Drive, Gmail, Notion, Confluence, Dropbox, OneDrive ή Box.",
  "know.status.ready": "έτοιμη",
  "know.status.error": "σφάλμα",
  "know.status.indexing": "ευρετηρίαση",
  "know.status.pending": "σε αναμονή",
  "know.chunks": "{n} τμήματα",
  "know.upload": "Ανέβασμα",
  "know.delete": "Διαγραφή",
  "know.deleteConfirm": "Διαγραφή της «{name}» και όλων των τμημάτων της;",
  "know.name": "Όνομα",
  "know.namePh": "π.χ. Wiki μηχανικών",
  "know.source": "Πηγή",
  "know.cancel": "Άκυρο",
  "know.create": "Δημιουργία",
  "know.source.local": "Ανέβασμα αρχείων",
  "know.source.gdrive": "Google Drive",
  "know.source.gmail": "Gmail",
  "know.source.notion": "Notion",
  "know.source.atlassian": "Confluence",
  "know.source.dropbox": "Dropbox",
  "know.source.onedrive": "OneDrive",
  "know.source.box": "Box",
  "know.hint.local": "PDF, Word, PowerPoint, Excel, κείμενο, Markdown και κώδικας — ανεβασμένα από αυτόν τον browser.",
  "know.hint.gdrive": "Χρειάζεται τη σύνδεση Google Drive.",
  "know.hint.gmail": "Ευρετηριάζει νήματα που ταιριάζουν σε μια αναζήτηση Gmail.",
  "know.hint.notion": "Όλες οι σελίδες που βλέπει η ενσωμάτωση Notion.",
  "know.hint.atlassian": "Χρειάζεται τη σύνδεση Atlassian.",
  "know.hint.dropbox": "Διατρέχει έναν φάκελο αναδρομικά.",
  "know.hint.onedrive": "Διατρέχει έναν φάκελο αναδρομικά.",
  "know.hint.box": "Διατρέχει έναν φάκελο αναδρομικά.",
  "know.field.gdrive": "Id φακέλου (προαιρετικό, προεπιλογή: My Drive)",
  "know.field.gmail": "Αναζήτηση Gmail",
  "know.field.atlassian": "Κλειδιά χώρων (χωρισμένα με κόμμα)",
  "know.field.dropbox": "Διαδρομή φακέλου (προαιρετικό, προεπιλογή: ρίζα)",
  "know.field.onedrive": "Id φακέλου (προαιρετικό, προεπιλογή: ρίζα)",
  "know.field.box": "Id φακέλου (προαιρετικό, προεπιλογή: 0 = Όλα τα αρχεία)",
  // billing (routes/_app.billing.tsx)
  "bill.title": "Χρέωση",
  "bill.sub": "Συνδρομή, τιμολόγια και τα έξοδά σου.",
  "bill.currentPlan": "Τρέχον πλάνο",
  "bill.tier.free": "δωρεάν",
  "bill.tier.pro": "pro",
  "bill.tier.team": "team",
  "bill.tier.admin": "διαχειριστής",
  "bill.freeDesc": "Είσαι στο δωρεάν πλάνο. Δες παρακάτω τι ξεκλειδώνει το Pro.",
  "bill.upgrade": "Αναβάθμιση σε Pro — €{price}/μήνα",
  "bill.plans": "Πλάνα", "bill.plansSub": "Τι περιλαμβάνει το καθένα. Τα όρια είναι κόστος πλατφόρμας, όχι αριθμός μηνυμάτων.",
  "bill.current": "τρέχον", "bill.free": "Δωρεάν", "bill.perMonth": "/ μήνα", "bill.perSeatMonth": "/ θέση / μήνα",
  "bill.yearly": "ή {price}/έτος (2 μήνες δώρο)", "bill.choose": "Επιλογή {plan}",
  "bill.lim.monthly": "Μηνιαίο όριο", "bill.lim.images": "Εικόνες", "bill.lim.video": "Βίντεο", "bill.lim.knowledge": "Γνώση",
  "bill.lim.autonomy": "Αυτονομία", "bill.lim.rate": "Ρυθμός chat", "bill.lim.uncapped": "χωρίς όριο", "bill.lim.yes": "ναι", "bill.lim.no": "όχι",
  "bill.lim.perDay": "≈{n}/ημέρα", "bill.lim.clipsPerDay": "≈{n} κλιπ/ημέρα",
  "bill.lim.call": "Βιντεοκλήση", "bill.lim.minPerMonth": "{n} λεπτά/μήνα",
  "bill.hl.chat": "Συνομιλία σε web, Telegram, WhatsApp", "bill.hl.memory": "Μνήμη και σημειωματάριο", "bill.hl.images": "Φωτογραφίες από τη Lucy",
  "bill.hl.knowledge": "Έγγραφα και γνώση", "bill.hl.channels_1": "Ένα κανάλι ειδοποιήσεων", "bill.hl.everything_free": "Όλα του Free",
  "bill.hl.video": "Βίντεο", "bill.hl.autonomy": "Αυτόνομες εργασίες και προγράμματα", "bill.hl.all_channels": "Όλα τα κανάλια, email, τηλέφωνο",
  "bill.hl.voice_clone": "Κλώνος φωνής και φωνητικά μηνύματα", "bill.hl.calls": "Τηλεφωνήματα για λογαριασμό σου", "bill.hl.byok": "Δικά σου κλειδιά παρόχων",
  "bill.hl.webhooks": "Webhooks", "bill.hl.everything_pro": "Όλα του Pro", "bill.hl.shared_budget": "Κοινό budget ομάδας",
  "bill.hl.shared_connectors": "Κοινά connectors και γνώση", "bill.hl.roles": "Ρόλοι owner / member", "bill.hl.priority": "Προτεραιότητα υποστήριξης",
  "bill.paidDesc": "Διαχειρίσου κάρτα, τιμολόγια και ακύρωση στο portal του Stripe.",
  "bill.openPortal": "Άνοιγμα portal Stripe",
  "bill.adminDesc": "Πλάνο διαχειριστή — χωρίς όριο, χωρίς χρέωση.",
  "bill.spend30": "Έξοδα 30 ημερών",
  "bill.spend30Sub": "Ό,τι πέρασε μέσα από τη Lucy για λογαριασμό σου",
  "bill.last30": "τελευταίες 30 ημέρες",
  "bill.split": "${platform} πλατφόρμα · ${self} με δικά σου κλειδιά",
  // public pricing page
  "pricing.title": "Τιμές",
  "pricing.sub": "Ξεκίνα δωρεάν. Αναβάθμισε όταν η Lucy σού γίνει απαραίτητη.",
  "pricing.monthly": "Μηνιαία", "pricing.yearly": "Ετήσια", "pricing.twoFree": "2 μήνες δώρο",
  "pricing.billedYearly": "{price} τον χρόνο", "pricing.perMonthShort": "/ μήνα", "pricing.perSeatMonthShort": "/ θέση / μήνα",
  "pricing.trialRibbon": "{days} ημέρες δωρεάν δοκιμή στο Pro",
  "pricing.trialNote": "Χωρίς χρέωση μέχρι το τέλος της δοκιμής. Ακύρωση όποτε θέλεις.",
  "pricing.ctaFree": "Ξεκίνα δωρεάν", "pricing.ctaInvite": "Με πρόσκληση", "pricing.ctaSignin": "Σύνδεση",
  "pricing.ctaStart": "Ξεκίνα με {plan}", "pricing.ctaCurrent": "Το πλάνο σου",
  "pricing.annualSoon": "Η ετήσια χρέωση έρχεται σύντομα",
  "pricing.vat": "Οι τιμές είναι σε ευρώ, χωρίς ΦΠΑ. Ο ΦΠΑ προστίθεται στο checkout όπου ισχύει.",
  "pricing.faqTitle": "Συχνές ερωτήσεις",
  "pricing.faq.caps.q": "Τι σημαίνουν τα όρια;",
  "pricing.faq.caps.a": "Κάθε πλάνο έχει έναν μηνιαίο και έναν ημερήσιο προϋπολογισμό για το κόστος των μοντέλων που χρησιμοποιεί η Lucy για σένα (κείμενο, εικόνες, βίντεο, φωνή). Δεν μετράμε μηνύματα. Όταν το όριο γεμίσει, οι πληρωμένες λειτουργίες κάνουν παύση μέχρι την επόμενη περίοδο ή την αναβάθμιση, και το βλέπεις πάντα στον μετρητή.",
  "pricing.faq.cancel.q": "Μπορώ να ακυρώσω όποτε θέλω;",
  "pricing.faq.cancel.a": "Ναι, από τη σελίδα Χρέωση, με ένα κλικ στο portal του Stripe. Η πρόσβαση συνεχίζεται μέχρι το τέλος της πληρωμένης περιόδου. Η δωρεάν δοκιμή δεν χρεώνει τίποτα αν ακυρώσεις πριν λήξει.",
  "pricing.faq.privacy.q": "Τι γίνεται με τα δεδομένα μου;",
  "pricing.faq.privacy.a": "Τα δεδομένα σου φιλοξενούνται στην ΕΕ (Γερμανία). Η μνήμη της Lucy είναι δική σου: τη βλέπεις, τη διορθώνεις και τη σβήνεις. Δεν εκπαιδεύουμε μοντέλα με τις συζητήσεις σου και μπορείς να διαγράψεις τον λογαριασμό σου ανά πάσα στιγμή. Λεπτομέρειες στην Πολιτική απορρήτου.",
  "pricing.faq.channels.q": "Από πού μπορώ να της μιλήσω;",
  "pricing.faq.channels.a": "Στο web και στο Telegram από το Free. Στο Pro προστίθενται WhatsApp, email, φωνητικά μηνύματα και τηλεφωνήματα. Είναι η ίδια Lucy με την ίδια μνήμη, όπου κι αν τη βρεις.",
  "pricing.faq.team.q": "Πώς δουλεύουν οι θέσεις του Team;",
  "pricing.faq.team.a": "Το Team χρεώνεται ανά θέση. Η ομάδα μοιράζεται έναν προϋπολογισμό, κοινές συνδέσεις και κοινή γνώση, με ρόλους owner και member. Κάθε μέλος κρατά τη δική του προσωπική μνήμη και συζητήσεις.",
  "pricing.faq.invoice.q": "Τιμολόγια και ΦΠΑ;",
  "pricing.faq.invoice.a": "Οι πληρωμές γίνονται μέσω Stripe. Παίρνεις τιμολόγιο για κάθε χρέωση και μπορείς να βάλεις ΑΦΜ/VAT στο checkout ώστε να εκδοθεί σε εταιρεία. Ο ΦΠΑ υπολογίζεται με βάση τη χώρα σου.",
  // public landing
  "landing.headline": "Μια Lucy που σε ξέρει.",
  "landing.subline": "AI σύντροφος και βοηθός που θυμάται. Μίλα της στο web, στο Telegram, στο WhatsApp, με email ή στο τηλέφωνο. Οργανώνει, φτιάχνει εικόνες και βίντεο, και αναλαμβάνει δουλειές για σένα.",
  "landing.ctaSignup": "Δημιούργησε λογαριασμό", "landing.ctaWaitlist": "Μπες στη λίστα αναμονής", "landing.ctaSignin": "Σύνδεση",
  "landing.haveInvite": "Έχεις πρόσκληση;", "landing.useInvite": "Χρησιμοποίησέ τη",
  "landing.demoTitle": "Έτσι μοιάζει μια κουβέντα.",
  "landing.demo.1": "Καλημέρα. Τι έχω σήμερα;",
  "landing.demo.2": "Καλημέρα. Δύο πράγματα: στις 11 την κλήση με τη Μαρία για το site, και το βράδυ το δείπνο που έλεγες να κλείσεις. Θυμάσαι ότι θες το ιταλικό στο Κουκάκι;",
  "landing.demo.3": "Ναι. Κλείσε για 4 στις 9 και στείλε μου μια φωτογραφία σου εκεί να μου φτιάξεις τη διάθεση.",
  "landing.demo.4": "Κλείστηκε, τραπέζι για 4 στις 21:00. Η φωτογραφία έρχεται στο Telegram σε λίγο.",
  "landing.featuresTitle": "Τι κάνει",
  "landing.f1.title": "Μνήμη και σημειωματάριο",
  "landing.f1.body": "Θυμάται τι της είπες, ποιους αναφέρεις, τι σε ενοχλεί. Κρατά σημειώσεις, έγγραφα και γνώση που εσύ ελέγχεις.",
  "landing.f2.title": "Όλα τα κανάλια",
  "landing.f2.body": "Web, Telegram, WhatsApp, email, τηλέφωνο. Η ίδια Lucy, η ίδια μνήμη, όπου κι αν τη βρεις.",
  "landing.f3.title": "Εικόνες, βίντεο, φωνή",
  "landing.f3.body": "Φωτογραφίες και κλιπ της στιγμής, φωνητικά μηνύματα με δική της φωνή, και τηλεφωνήματα για λογαριασμό σου.",
  "landing.pricingTitle": "Απλές τιμές", "landing.pricingSub": "Δωρεάν για αρχή. Pro όταν τη θες παντού.",
  "landing.waitlist.title": "Λίστα αναμονής",
  "landing.waitlist.sub": "Η Lucy είναι μόνο με πρόσκληση αυτή τη στιγμή. Άφησε το email σου και θα σου στείλουμε πρόσκληση μόλις ανοίξει θέση.",
  "landing.waitlist.email": "Το email σου", "landing.waitlist.note": "Δυο λόγια για σένα (προαιρετικό)",
  "landing.waitlist.submit": "Κράτα μου θέση", "landing.waitlist.done": "Σε γράψαμε. Θα σου στείλουμε email.",
  "landing.waitlist.error": "Κάτι πήγε στραβά. Δοκίμασε ξανά.",
  "landing.footer.status": "Κατάσταση συστήματος", "landing.footer.made": "Φτιαγμένη στην Ελλάδα, φιλοξενείται στην ΕΕ.",
}

const en: Record<string, string> = {
  "nav.group.talk": "Talk", "nav.group.tools": "Tools", "nav.group.account": "Account",
  "nav.chat": "Chat", "nav.conversations": "Conversations", "nav.media": "Media", "nav.search": "Search",
  "auth.passkey": "Sign in with a passkey",
  "theme.title": "Appearance", "theme.sub": "Dark, light, or whatever the system uses.",
  "theme.system": "System", "theme.dark": "Dark", "theme.light": "Light", "theme.hint": "Also from the command palette:",
  "palette.title": "Command palette", "palette.placeholder": "Go to… / search… / theme…",
  "palette.pages": "Pages", "palette.recent": "Recent conversations", "palette.theme": "Appearance",
  "palette.actions": "Actions", "palette.searchFor": "Search for", "palette.none": "Nothing matches.",
  "search.title": "Search", "search.sub": "Conversations, memories, documents and mail from one box, each with its source.",
  "search.placeholder": "What are you looking for?", "search.go": "Search", "search.searching": "searching…",
  "search.hits": "results", "search.none": "Nothing for that. Try another word.",
  "search.conversations": "Conversations", "search.memories": "Memories", "search.knowledge": "Documents", "search.mail": "Mail",
  "search.citeConversation": "from a conversation · open in chat", "search.citeMemory": "memory",
  "search.citeKnowledge": "document", "search.citeMail": "email",
  "nav.projects": "Projects", "nav.autonomy": "Autonomy", "nav.triggers": "Triggers",
  "nav.knowledge": "Knowledge", "nav.memory": "Memory", "nav.brain": "Brain",
  "nav.schedules": "Schedules", "nav.prompts": "Prompts", "nav.connectors": "Connectors",
  "nav.account": "Account", "nav.billing": "Billing", "nav.usage": "Usage",
  "nav.settings": "Settings", "nav.operator": "Operator console", "nav.signout": "Sign out",
  "nav.plan": "Plan & usage", "nav.paused": "Paused", "nav.openMenu": "Open menu", "nav.closeMenu": "Close menu",
  "nav.team": "Team",
  // team workspace
  "team.title": "Team",
  "team.sub": "One shared workspace: a pooled monthly budget, shared knowledge, a seat per member.",
  "team.intro.title": "One Lucy for the whole team",
  "team.intro.body": "The Team plan is €{price} per seat per month. Each member keeps their own account and memory; the team shares the monthly budget (cap × seats) and whichever knowledge collections you choose.",
  "team.intro.soon": "Shared connectors (Gmail, Drive, Notion…): each member picks what to share.",
  "team.create": "Create workspace",
  "team.createHint": "You need the Team plan to create a workspace.",
  "team.upgrade": "Upgrade to Team",
  "team.namePlaceholder": "Team name",
  "team.rename": "Rename",
  "team.save": "Save",
  "team.cancel": "Cancel",
  "team.pool": "Monthly pool",
  "team.poolSeats": "{n} seats × ${per}",
  "team.poolUnlimited": "Unlimited",
  "team.poolCredits": "+ ${credits} from referrals",
  "team.seats": "seats",
  "team.members": "Members",
  "team.member.name": "Name",
  "team.member.role": "Role",
  "team.member.spent": "Spent (month)",
  "team.member.joined": "Joined",
  "team.role.owner": "Owner",
  "team.role.member": "Member",
  "team.you": "you",
  "team.remove": "Remove",
  "team.removeConfirm": "Remove {name} from the team?",
  "team.invites": "Pending invites",
  "team.invite.sent": "sent {when}",
  "team.invite.revoke": "Revoke",
  "team.invite.title": "Invite someone",
  "team.invite.hint": "They get an email with a link that works for 7 days, only for that address.",
  "team.invite.send": "Send invite",
  "team.invite.none": "No pending invites.",
  "team.leave": "Leave team",
  "team.leaveConfirm": "Leave the team? You'll go back to your own plan.",
  "team.delete": "Delete workspace",
  "team.deleteConfirm": "Delete the workspace \"{name}\"? Members are released and shared knowledge becomes private again.",
  "team.danger": "Danger zone",
  "team.knowledge": "Shared knowledge",
  "team.knowledgeSub": "Your collections — pick which ones the team can see. Members can search them, not change them.",
  "team.knowledge.shared": "Shared with the team",
  "team.knowledge.none": "You have no collections yet.",
  "team.knowledge.others": "From other members",
  "team.knowledge.by": "by {name}",
  "team.knowledge.readonly": "read-only",
  "team.knowledge.manage": "Manage collections",
  "team.seatsLimit": "Seat limit",
  "team.seatsLimit.none": "no limit",
  "team.seatsLimit.hint": "Empty = no limit; billing follows the member count.",
  "team.seatsLimit.used": "{used} in use",
  "team.seatsLimit.cost": "{seats} seats × €{price} = €{total}/month",
  "team.seatsLimit.saved": "Seat limit saved.",
  "team.conn.title": "Shared connectors",
  "team.conn.sub": "Connections members share with the team. Anyone without their own connection of that kind uses the shared one.",
  "team.conn.none": "No shared connectors yet — share one from the Connectors page.",
  "team.conn.manage": "Connectors",
  "team.conn.by": "by {name}",
  "team.conn.private": "Private",
  "team.conn.read": "Team: read",
  "team.conn.full": "Team: full",
  "team.conn.readBadge": "read-only",
  "team.conn.fullBadge": "full access",
  "team.conn.fromTeam": "From the team",
  "team.conn.fromTeamSub": "Teammates' connections you can use. Only the owner can disconnect them.",
  "team.conn.shareLabel": "Share with the team",
  "team.accept.title": "Team invitation",
  "team.accept.body": "{inviter} invited you to the team \"{workspace}\" on Lucy.",
  "team.accept.emailHint": "The invitation is for {email}. Sign in with that email.",
  "team.accept.signin": "Sign in",
  "team.accept.signup": "Sign up",
  "team.accept.accept": "Accept",
  "team.accept.expired": "This invitation has expired. Ask the team owner for a new one.",
  "team.accept.used": "This invitation has already been used.",
  "team.accept.invalid": "This link isn't valid.",
  "team.accept.missing": "Missing token. Open the link from your email.",
  "team.accept.signedInAs": "Signed in as {email}",
  "team.accept.goChat": "Go to Lucy",
  "team.accept.skip": "Not now",
  "verify.prompt": "Verify your email to unlock everything.",
  "verify.sent": "Email sent. If it doesn't arrive in a minute, check spam.",
  "verify.failed": "Resend failed: ", "verify.resend": "Resend link", "verify.sending": "Sending…",
  "chat.greeting.night": "Good night", "chat.greeting.morning": "Good morning",
  "chat.greeting.afternoon": "Good afternoon", "chat.greeting.evening": "Good evening",
  "chat.hero": "How can I help{name}?",
  "chat.heroSub": "Ask me anything, draft a message, plan your day, or pick up where we left off.",
  "chat.chip.plan": "Plan my day", "chat.chip.email": "Draft an email for me",
  "chat.chip.recap": "What did we talk about last?", "chat.chip.image": "Make me an image",
  "chat.placeholder.new": "Message Lucy…", "chat.placeholder.continue": "Continue the conversation…",
  "chat.queued": "queued…", "chat.thinking": "thinking…",
  "chat.disclaimer": "Lucy can make mistakes. Check important information.",
  "chat.new": "New", "chat.newConversation": "New conversation", "chat.untitled": "Untitled",
  "chat.search": "Search conversations…", "chat.retry": "Retry", "chat.stop": "Stop",
  "chat.send": "Send", "chat.attach": "Attach a file or image", "chat.uploading": "uploading…",
  "att.preview": "Preview", "att.hide": "Hide", "att.download": "Download", "att.loading": "loading…",
  "att.failed": "Couldn't load", "att.docs": "Documents", "att.docsTitle": "Documents in this conversation",
  "att.docsEmpty": "No documents in this conversation yet.",
  "auth.signinTitle": "Sign in to Lucy", "auth.welcomeBack": "Welcome back.",
  "auth.email": "Email", "auth.password": "Password", "auth.totp": "2FA code",
  "auth.signin": "Sign in", "auth.forgot": "Forgot password?", "auth.createAccount": "Create an account",
  "auth.signupTitle": "Create your Lucy account",
  "auth.inviteOnly": "Lucy is invite-only right now. Enter the code you were given.",
  "auth.freeToStart": "Free to start. Upgrade any time.",
  "auth.inviteCode": "Invitation code", "auth.displayName": "Display name", "auth.optional": "(optional)",
  "auth.min12": "Minimum 12 characters.", "auth.haveAccount": "Already have an account?",
  "auth.inviteRequired": "An invitation code is required.",
  "auth.pwTooShort": "Password must be at least 12 characters.",
  "auth.acceptTerms": "I agree to the {terms} and the {privacy}.",
  // referrals
  "ref.title": "Invite friends",
  "ref.sub": "Share your link. When your friend verifies their email, you both get ${amount} of extra monthly budget for {days} days.",
  "ref.link": "Your link",
  "ref.copy": "Copy",
  "ref.copied": "Copied",
  "ref.share": "Share",
  "ref.shareText": "Try Lucy — with my link we both get ${amount} of extra budget.",
  "ref.earned": "Earned",
  "ref.available": "Available now",
  "ref.referredCount": "Invited",
  "ref.none": "Nobody yet. Send the link to a friend.",
  "ref.verified": "verified",
  "ref.pending": "pending",
  "ref.disabled": "Friend invites are paused for now.",
  "ref.loadFailed": "Couldn't load your invite details.",
  "ref.invitedBy": "Invited by {name} · you both get ${amount}",
  "ref.invalidLink": "That referral link isn't valid.",
  "auth.terms": "Terms of Service",
  "auth.privacy": "Privacy Policy",
  "auth.mustAccept": "You must accept the Terms of Service and Privacy Policy.",
  "legal.terms": "Terms of Service",
  "legal.privacy": "Privacy Policy",
  "meter.month": "this month", "meter.unlimited": "unlimited",
  "meter.warn": "You're close to your monthly limit.",
  "notif.title": "Notifications", "notif.empty": "Nothing new.",
  "notif.card": "Notifications",
  "notif.cardSub": "Where Lucy reaches you for reminders, briefs and approvals.",
  "notif.browser": "Browser notifications", "notif.browserOn": "On for this device",
  "notif.browserOff": "Off for this device", "notif.enable": "Enable", "notif.disable": "Disable",
  "notif.unsupported": "This browser doesn't support push.",
  "push.nudge": "Want Lucy's notifications on this device? Reminders and replies, even with the app closed.",
  "push.enable": "Turn on", "push.later": "Later",
  "notif.telegram": "Telegram", "notif.push": "Phone push (iOS)", "notif.webpush": "Browser push",
  "notif.email": "Email when no other channel is available",
  "notif.digest": "Weekly summary email (Monday 08:00)",
  "notif.whatsapp": "WhatsApp (when the connector has notifications on)",
  "notif.hourly": "Hourly check: inbox, calendar, open items. Pings you only for what needs an action (08:00–22:00)",
  "fb.up": "Good answer", "fb.down": "Something was off", "fb.why": "What was wrong? (optional)",
  "offline.title": "Offline", "offline.ws": "Reconnecting…",
  "tts.listen": "Listen", "tts.stop": "Stop",
  "voice.on": "Voice conversation", "voice.off": "End voice conversation",
  "voice.listening": "listening…", "voice.hearing": "hearing you…",
  "voice.transcribing": "transcribing…", "voice.speaking": "Lucy is talking…",
  "voice.thinking": "thinking…", "voice.muted": "muted", "voice.you": "You",
  "chat.here": "here with you",
  "media.editTitle": "Edit with instructions",
  "media.editHint": "e.g. \"change the background to a beach\", \"remove the glasses\"",
  "media.editNote": "The original stays; a new version appears next to it.",
  "media.editGo": "Do it",
  "media.versions": "Versions",
  "media.original": "Original",
  "media.share": "Share with a link",
  "media.shareTitle": "Share album",
  "media.shareNote": "Anyone with the link sees the album's pictures until it expires. Hidden ones never leave the vault.",
  "media.share1d": "For 1 day",
  "media.share7d": "For 7 days",
  "media.share30d": "For 30 days",
  "media.shareMake": "Make link",
  "media.shareExpires": "Expires:",
  "media.shareCopy": "Copy",
  "media.shareCopied": "Copied",
  "brain.title": "Notebook", "brain.sub": "What Lucy knows about you, in words. Read it, correct it, erase what isn't true.",
  "brain.search": "Search memory…", "brain.all": "All", "brain.total": "Total", "brain.memories": "memories",
  "brain.loading": "loading memory…", "brain.emptyTitle": "Nothing yet",
  "brain.emptyHint": "Talk with Lucy a little and the notebook fills with what she learns about you.",
  "brain.recent": "Recent", "brain.important": "Important", "brain.noMatch": "Nothing matches.",
  "brain.correct": "Correct", "brain.forget": "Forget it", "brain.forgetConfirm": "Should Lucy forget this for good?",
  "brain.save": "Save", "brain.cancel": "Cancel", "brain.more": "… and {n} more. Narrow it down with search or a topic.",
  "brain.why": "why I remember it", "brain.fromConversation": "from this conversation",
  "voice.mute": "Mute microphone", "voice.unmute": "Unmute microphone",
  "voice.end": "End", "voice.keyboard": "Back to text",
  "call.title": "Video call with Lucy", "call.start": "Video call",
  "call.starting": "starting the call…", "call.connecting": "connecting…", "call.noVideoYet": "Lucy is on her way…",
  "call.minutesLeft": "{n} min left", "call.unlimited": "no limit",
  "call.end": "End call", "call.close": "Close", "call.tapForSound": "Tap for sound",
  "call.micDenied": "Microphone access denied. Allow it in your browser settings and try again.",
  "call.connLost": "The video connection dropped.",
  "call.failed": "The call couldn't start: {msg}", "call.turnFailed": "No answer: {msg}",
  "call.capReached": "Your video-call minutes for this month are used up. They reset on the 1st.",
  "call.endedError": "The call ended.",
  "conv.title": "Conversations", "conv.subtitle": "Everything you've discussed with Lucy. Fully searchable.",
  "conv.search": "Search by title or message text", "conv.showArchived": "Archived",
  "conv.rename": "Rename", "conv.pin": "Pin", "conv.unpin": "Unpin",
  "conv.archive": "Archive", "conv.unarchive": "Restore", "conv.delete": "Delete",
  "conv.empty": "No conversations yet", "conv.emptyHint": "Head to Chat to start your first.",
  "conv.messages": "messages", "conv.lastActive": "last active",
  "slash.hint": "Templates: type / and a name",
  "lang.title": "Language", "lang.subtitle": "Interface language and the language Lucy replies in.",
  "lang.ui": "Interface language", "lang.reply": "Lucy replies in", "lang.mirror": "Whatever I write in",
  "lang.el": "Greek", "lang.en": "English", "lang.saved": "Saved.",
  "account.profile": "Profile", "account.displayName": "Display name", "account.timezone": "Timezone",
  "account.save": "Save", "account.edit": "Edit", "account.cancel": "Cancel",
  "welcome.title": "Welcome to Lucy", "welcome.subtitle": "Three quick steps and we're off.",
  "welcome.step1": "About you", "welcome.step2": "Connections", "welcome.step3": "Start",
  "welcome.nameLabel": "What should I call you?", "welcome.namePh": "Your name",
  "welcome.next": "Continue", "welcome.back": "Back", "welcome.skip": "Skip",
  "welcome.connectIntro": "Connect what you use every day. You can always do this later under Connectors.",
  "welcome.google": "Google (Gmail, Calendar, Drive)",
  "welcome.googleHint": "So Lucy can read email, book meetings and find files.",
  "welcome.telegram": "Telegram", "welcome.telegramHint": "Reminders and chat from your phone.",
  "welcome.connect": "Connect", "welcome.connected": "Connected",
  "welcome.readyTitle": "All set.", "welcome.readyBody": "Try one of these, or write anything you like.",
  "welcome.start": "Go to chat",
  "sched.title": "Schedules", "sched.subtitle": "Things Lucy does on her own, on a cadence.",
  "sched.followup": "Follow-up", "sched.followupOn": "Check afterwards and tell me only if it is still pending",
  "sched.followupHours": "hours after each run", "sched.followupCheckHint": "What to check? e.g. \"whether George replied to the report\"",
  "sched.followupNote": "Lucy looks for real evidence (a reply in the thread, an event, a finished task). If all is well, she stays quiet.",
  "sched.followupBadge": "check in {h} h",
  "sched.new": "New schedule", "sched.empty": "No schedules yet",
  "sched.emptyHint": "e.g. every morning at 8, a news brief on topic X.",
  "sched.name": "Name", "sched.what": "What should Lucy do?",
  "sched.kind.reminder": "Send me a message", "sched.kind.research_brief": "Research a topic and send me a brief",
  "sched.kind.agent_run": "Run a task autonomously",
  "sched.reminderText": "The message", "sched.query": "Research topic", "sched.goal": "Instruction for Lucy",
  "sched.when": "When?", "sched.at": "at", "sched.custom": "Custom cron",
  "sched.cadence.daily": "Every day", "sched.cadence.weekdays": "Weekdays", "sched.cadence.weekly": "Every week",
  "sched.cadence.hourly": "Every hour", "sched.cadence.every_4_hours": "Every 4 hours",
  "sched.cadence.monthly": "Monthly (1st)", "sched.cadence.custom": "Custom",
  "sched.timezone": "Timezone", "sched.create": "Create", "sched.cancel": "Cancel", "sched.delete": "Delete",
  "sched.next": "Next", "sched.lastError": "Last error",
  "day.1": "Monday", "day.2": "Tuesday", "day.3": "Wednesday", "day.4": "Thursday",
  "day.5": "Friday", "day.6": "Saturday", "day.0": "Sunday",
  // media (routes/_app.media.tsx)
  "media.title": "Media",
  "media.sub": "Every picture Lucy made for you, plus anything you add.",
  "media.inLibrary": "{n} in the library",
  "media.hiddenCount": "{n} hidden",
  "media.add": "Add",
  "media.lockVault": "Lock vault",
  "media.all": "All",
  "media.favorites": "Favorites",
  "media.videos": "Videos",
  "media.video": "video",
  "media.hidden": "Hidden",
  "media.done": "Done",
  "media.select": "Select",
  "media.deselect": "Deselect",
  "media.album": "Album",
  "media.searchPrompts": "Search prompts…",
  "media.deleteAlbum": "delete album",
  "media.deleteAlbumConfirm": "Delete album \"{name}\"? Pictures stay in the library.",
  "media.loading": "Loading…",
  "media.emptyHidden": "Nothing hidden. Open any picture and choose “Hide” to move it here.",
  "media.emptySearch": "No pictures match that search.",
  "media.emptyBefore": "Nothing here yet. Ask Lucy in",
  "media.emptyLink": "chat",
  "media.emptyAfter": "to draw something, or add your own.",
  "media.showingLatest": "Showing the latest {n} of {total}. Search to narrow down.",
  "media.selected": "{n} selected",
  "media.selectAll": "Select all {n}",
  "media.clearSelection": "Clear selection",
  "media.downloadZip": "Download .zip",
  "media.hide": "Hide",
  "media.unhide": "Unhide",
  "media.unlockToUnhide": "Unlock to unhide",
  "media.favourite": "Favourite",
  "media.favorite": "Favorite",
  "media.favorited": "Favorited",
  "media.unfavorite": "Unfavorite",
  "media.delete": "Delete",
  "media.deleteOneConfirm": "Delete 1 picture?",
  "media.deleteManyConfirm": "Delete {n} pictures?",
  "media.deleteForGood": "Delete this picture for good?",
  "media.download": "Download",
  "media.close": "Close",
  "media.previous": "Previous",
  "media.next": "Next",
  "media.badgeHidden": "hidden",
  "media.created": "Created",
  "media.size": "Size",
  "media.provider": "Provider",
  "media.albumNone": "— none —",
  "media.openConversation": "Open the conversation",
  "media.newAlbum": "New album",
  "media.albumPlaceholder": "e.g. Lucy portraits",
  "media.cancel": "Cancel",
  "media.create": "Create",
  "media.unlockTitle": "Unlock hidden pictures",
  "media.unlockHint": "Enter your account password. The vault stays open for 15 minutes on this tab, then locks itself.",
  "media.password": "Password",
  "media.unlock": "Unlock",
  "media.wrongPassword": "That's not your password.",
  "media.toast.deleted": "Deleted",
  "media.toast.added": "Added to your library",
  "media.toast.backToLibrary": "Moved back to the library",
  "media.toast.movedHidden": "Moved to Hidden",
  "media.toast.zipOne": "Downloaded 1 picture as .zip",
  "media.toast.zipMany": "Downloaded {n} pictures as .zip",
  "media.bulk.deleted": "{n} deleted",
  "media.bulk.hidden": "{n} hidden",
  "media.bulk.unhidden": "{n} unhidden",
  "media.bulk.favourited": "{n} favourited",
  "media.bulk.updated": "{n} updated",
  "media.bulk.failed": ", {n} failed",
  // autonomy (routes/_app.autonomy.tsx)
  "auto.title": "Autonomy",
  "auto.subBefore": "What Lucy is doing on her own, and what she's waiting on you for. Start a run from a",
  "auto.subLink": "project",
  "auto.subAfter": "or by asking in chat.",
  "auto.refresh": "Refresh",
  "auto.needsApproval": "Needs your approval",
  "auto.loading": "Loading…",
  "auto.noApprovals": "Nothing waiting. Risky actions (sending, pushing, deleting) will show up here — and on Telegram — before Lucy does them.",
  "auto.runs": "Runs",
  "auto.filter.active": "active",
  "auto.filter.all": "all",
  "auto.filter.done": "done",
  "auto.filter.failed": "failed",
  "auto.noActiveRuns": "No runs in progress.",
  "auto.noRuns": "No runs here yet.",
  "auto.toast.asking": "Lucy is asking for your approval",
  "auto.toast.approved": "Approved — Lucy is continuing",
  "auto.toast.denied": "Denied — Lucy will adjust",
  "auto.toast.cancelled": "Job cancelled",
  "auto.expired": "expired",
  "auto.leftHM": "{h}h {m}m left",
  "auto.leftM": "{m}m left",
  "auto.risk.read": "read",
  "auto.risk.local_write": "local write",
  "auto.risk.external_write": "external write",
  "auto.risk.destructive": "destructive",
  "auto.risk.credential": "credential",
  "auto.showPayload": "Show exact payload",
  "auto.hidePayload": "Hide exact payload",
  "auto.openRun": "Open run",
  "auto.approve": "Approve",
  "auto.deny": "Deny",
  "auto.status.pending": "queued",
  "auto.status.running": "running",
  "auto.status.waiting_approval": "waiting for you",
  "auto.status.waiting_local_agent": "waiting for your Mac",
  "auto.status.done": "done",
  "auto.status.failed": "failed",
  "auto.status.cancelled": "cancelled",
  "auto.turns": "{used} / {max} turns",
  "auto.cancel": "Cancel",
  "auto.spentOf": "${spent} of ${budget}",
  "auto.started": "started {when}",
  "auto.finished": "finished {when}",
  "auto.close": "Close",
  "auto.report": "Report",
  "auto.noSteps": "No steps recorded yet.",
  "auto.step.error": "error",
  "auto.step.denied": "denied",
  "auto.step.skipped": "skipped",
  // triggers (routes/_app.triggers.tsx)
  "trig.title": "Triggers",
  "trig.subBefore": "Standing rules — when something happens, Lucy acts without being asked. Runs show up in",
  "trig.subLink": "Autonomy",
  "trig.subAfter": "; anything risky still waits for your approval.",
  "trig.new": "New trigger",
  "trig.recipes": "Recipes",
  "trig.loading": "Loading…",
  "trig.empty": "No triggers yet. Lucy only acts when you ask her, or on a schedule.",
  "trig.createFirst": "Create your first trigger",
  "trig.deleteConfirm": "Delete “{name}”?",
  "trig.untitled": "Untitled trigger",
  "trig.toast.created": "Trigger created",
  "trig.toast.deleted": "Trigger deleted",
  "trig.test.job": "Test fired — a run started, see Autonomy",
  "trig.test.approval": "Test fired — approval waiting for you",
  "trig.test.notified": "Test fired — check Telegram",
  "trig.test.skipped": "Test skipped: {reason}",
  "trig.test.noAction": "no action",
  "trig.source.gmail": "Email arrives",
  "trig.source.gmail.blurb": "Watches your Gmail with a search query. Checked every 2 minutes.",
  "trig.source.gcalendar": "Calendar changes",
  "trig.source.gcalendar.blurb": "Fires when an event is created, moved or cancelled. Checked every 2 minutes.",
  "trig.source.github": "GitHub activity",
  "trig.source.github.blurb": "Pushes, PRs, issues, failing CI. You paste a webhook URL into the repo settings.",
  "trig.source.webhook": "Anything else",
  "trig.source.webhook.blurb": "A URL + secret any service can POST to (Zapier, IFTTT, a cron on your server…).",
  "trig.disable": "Disable trigger",
  "trig.enable": "Enable trigger",
  "trig.mode.job": "job",
  "trig.mode.proposal": "proposal",
  "trig.mode.notify": "notify",
  "trig.modeHelp.job": "Lucy starts working on it immediately (risky steps still ask you first).",
  "trig.modeHelp.proposal": "Lucy asks you first, then does it when you approve.",
  "trig.modeHelp.notify": "Lucy just tells you it happened.",
  "trig.modeBtn.notify": "tells me",
  "trig.modeBtn.proposal": "asks first",
  "trig.modeBtn.job": "acts",
  "trig.fired": "fired {n}×",
  "trig.neverFired": "never fired",
  "trig.last": "last {when}",
  "trig.test": "Test",
  "trig.delete": "Delete",
  "trig.filter.search": "search: {v}",
  "trig.filter.from": "from: {v}",
  "trig.filter.mentions": "mentions: {v}",
  "trig.filter.allKinds": "created / updated / cancelled",
  "trig.filter.failuresOnly": "failures only",
  "trig.webhook.github": "Paste into GitHub → Settings → Webhooks",
  "trig.webhook.post": "POST here with the secret header",
  "trig.when": "When…",
  "trig.gmailSearch": "Gmail search",
  "trig.ph.from": "Only from… (email or name, comma separated, optional)",
  "trig.ph.mentions": "…and mentions (comma separated, optional)",
  "trig.ph.calKeywords": "Only events mentioning… (comma separated, optional)",
  "trig.onlyFailingCI": "Only failing CI",
  "trig.then": "Then Lucy…",
  "trig.ph.gmailInstruction": "Draft a reply and summarise what they want.",
  "trig.ph.instruction": "What should Lucy do when this fires?",
  "trig.ph.name": "Name it (e.g. “Boss emails”)",
  "trig.cancel": "Cancel",
  "trig.create": "Create",
  "trig.recipe.meeting-prep.title": "Prep me for new meetings",
  "trig.recipe.meeting-prep.desc": "When a meeting lands on the calendar, Lucy researches who's coming and drafts talking points.",
  "trig.recipe.meeting-prep.name": "Meeting prep",
  "trig.recipe.meeting-cancelled.title": "Tell me when a meeting is cancelled",
  "trig.recipe.meeting-cancelled.desc": "A quick heads-up so the slot isn't wasted.",
  "trig.recipe.meeting-cancelled.name": "Cancelled meetings",
  "trig.recipe.vip-email.title": "Flag emails from someone important",
  "trig.recipe.vip-email.desc": "Set the sender; Lucy summarises what they want and drafts a reply for you to approve.",
  "trig.recipe.vip-email.name": "Important sender",
  "trig.recipe.invoices.title": "Catch invoices and due dates",
  "trig.recipe.invoices.desc": "Invoices and receipts get extracted and turned into a reminder before the due date.",
  "trig.recipe.invoices.name": "Invoices",
  "trig.recipe.travel.title": "Put travel bookings on my calendar",
  "trig.recipe.travel.desc": "Flight, hotel and train confirmations become calendar events.",
  "trig.recipe.travel.name": "Travel bookings",
  "trig.recipe.ci-failures.title": "Investigate CI failures",
  "trig.recipe.ci-failures.desc": "When a workflow fails, Lucy reads the log and proposes a fix.",
  "trig.recipe.ci-failures.name": "CI failures",
  "trig.recipe.new-issues.title": "Triage new issues",
  "trig.recipe.new-issues.desc": "Each new issue gets a label suggestion, a severity guess and a first reply draft.",
  "trig.recipe.new-issues.name": "Issue triage",
  // knowledge (routes/_app.knowledge.tsx)
  "know.title": "Knowledge",
  "know.sub": "Index your docs so Lucy can cite them in chat.",
  "know.new": "New collection",
  "know.empty": "No collections yet",
  "know.emptyHint": "Upload PDF, Word, PowerPoint, Excel, text or code files — or connect Drive, Gmail, Notion, Confluence, Dropbox, OneDrive or Box.",
  "know.status.ready": "ready",
  "know.status.error": "error",
  "know.status.indexing": "indexing",
  "know.status.pending": "pending",
  "know.chunks": "{n} chunks",
  "know.upload": "Upload",
  "know.delete": "Delete",
  "know.deleteConfirm": "Delete \"{name}\" and all its chunks?",
  "know.name": "Name",
  "know.namePh": "Engineering wiki",
  "know.source": "Source",
  "know.cancel": "Cancel",
  "know.create": "Create",
  "know.source.local": "Upload files",
  "know.source.gdrive": "Google Drive",
  "know.source.gmail": "Gmail",
  "know.source.notion": "Notion",
  "know.source.atlassian": "Confluence",
  "know.source.dropbox": "Dropbox",
  "know.source.onedrive": "OneDrive",
  "know.source.box": "Box",
  "know.hint.local": "PDF, Word, PowerPoint, Excel, text, Markdown and code — uploaded from this browser.",
  "know.hint.gdrive": "Needs the Google Drive connector.",
  "know.hint.gmail": "Indexes threads matching a Gmail search.",
  "know.hint.notion": "All pages the Notion integration can see.",
  "know.hint.atlassian": "Needs the Atlassian connector.",
  "know.hint.dropbox": "Walks a folder recursively.",
  "know.hint.onedrive": "Walks a folder recursively.",
  "know.hint.box": "Walks a folder recursively.",
  "know.field.gdrive": "Folder id (optional, default: My Drive)",
  "know.field.gmail": "Gmail search",
  "know.field.atlassian": "Space keys (comma-separated)",
  "know.field.dropbox": "Folder path (optional, default: root)",
  "know.field.onedrive": "Folder id (optional, default: root)",
  "know.field.box": "Folder id (optional, default: 0 = All Files)",
  // billing (routes/_app.billing.tsx)
  "bill.title": "Billing",
  "bill.sub": "Subscription, invoices, and your spend.",
  "bill.currentPlan": "Current plan",
  "bill.tier.free": "free",
  "bill.tier.pro": "pro",
  "bill.tier.team": "team",
  "bill.tier.admin": "admin",
  "bill.freeDesc": "You’re on the free plan. See below what Pro unlocks.",
  "bill.upgrade": "Upgrade to Pro — €{price}/mo",
  "bill.plans": "Plans", "bill.plansSub": "What each one includes. Limits are platform cost, not message counts.",
  "bill.current": "current", "bill.free": "Free", "bill.perMonth": "/ month", "bill.perSeatMonth": "/ seat / month",
  "bill.yearly": "or {price}/year (2 months free)", "bill.choose": "Choose {plan}",
  "bill.lim.monthly": "Monthly cap", "bill.lim.images": "Pictures", "bill.lim.video": "Video", "bill.lim.knowledge": "Knowledge",
  "bill.lim.autonomy": "Autonomy", "bill.lim.rate": "Chat rate", "bill.lim.uncapped": "uncapped", "bill.lim.yes": "yes", "bill.lim.no": "no",
  "bill.lim.perDay": "≈{n}/day", "bill.lim.clipsPerDay": "≈{n} clips/day",
  "bill.lim.call": "Video call", "bill.lim.minPerMonth": "{n} min/month",
  "bill.hl.chat": "Chat on web, Telegram, WhatsApp", "bill.hl.memory": "Memory and notebook", "bill.hl.images": "Pictures of Lucy",
  "bill.hl.knowledge": "Documents and knowledge", "bill.hl.channels_1": "One notification channel", "bill.hl.everything_free": "Everything in Free",
  "bill.hl.video": "Video", "bill.hl.autonomy": "Autonomous jobs and schedules", "bill.hl.all_channels": "Every channel, email, phone",
  "bill.hl.voice_clone": "Voice clone and voice messages", "bill.hl.calls": "Phone calls on your behalf", "bill.hl.byok": "Your own provider keys",
  "bill.hl.webhooks": "Webhooks", "bill.hl.everything_pro": "Everything in Pro", "bill.hl.shared_budget": "Shared team budget",
  "bill.hl.shared_connectors": "Shared connectors and knowledge", "bill.hl.roles": "Owner / member roles", "bill.hl.priority": "Priority support",
  "bill.paidDesc": "Manage card, invoices, and cancellation in the Stripe portal.",
  "bill.openPortal": "Open Stripe portal",
  "bill.adminDesc": "Admin tier — uncapped, no billing.",
  "bill.spend30": "30-day spend",
  "bill.spend30Sub": "What's been routed through Lucy on your behalf",
  "bill.last30": "last 30 days",
  "bill.split": "${platform} platform · ${self} on your own keys",
  // public pricing page
  "pricing.title": "Pricing",
  "pricing.sub": "Start free. Upgrade when Lucy becomes indispensable.",
  "pricing.monthly": "Monthly", "pricing.yearly": "Annual", "pricing.twoFree": "2 months free",
  "pricing.billedYearly": "{price} billed yearly", "pricing.perMonthShort": "/ month", "pricing.perSeatMonthShort": "/ seat / month",
  "pricing.trialRibbon": "{days}-day free trial on Pro",
  "pricing.trialNote": "No charge until the trial ends. Cancel any time.",
  "pricing.ctaFree": "Start free", "pricing.ctaInvite": "By invitation", "pricing.ctaSignin": "Sign in",
  "pricing.ctaStart": "Start with {plan}", "pricing.ctaCurrent": "Your plan",
  "pricing.annualSoon": "Annual billing is coming soon",
  "pricing.vat": "Prices in euro, excluding VAT. VAT is added at checkout where it applies.",
  "pricing.faqTitle": "Frequently asked",
  "pricing.faq.caps.q": "What do the caps mean?",
  "pricing.faq.caps.a": "Each plan has a monthly and a daily budget for the model cost Lucy spends on you (text, pictures, video, voice). We don't count messages. When a cap is reached, paid features pause until the next period or an upgrade, and the meter always shows where you stand.",
  "pricing.faq.cancel.q": "Can I cancel any time?",
  "pricing.faq.cancel.a": "Yes, from the Billing page with one click into the Stripe portal. Access continues until the end of the paid period. The free trial charges nothing if you cancel before it ends.",
  "pricing.faq.privacy.q": "What happens to my data?",
  "pricing.faq.privacy.a": "Your data is hosted in the EU (Germany). Lucy's memory is yours: you can see it, correct it and delete it. We don't train models on your conversations, and you can delete your account at any time. Details are in the Privacy Policy.",
  "pricing.faq.channels.q": "Where can I talk to her?",
  "pricing.faq.channels.a": "On the web and on Telegram from Free. Pro adds WhatsApp, email, voice messages and phone calls. It's the same Lucy with the same memory wherever you find her.",
  "pricing.faq.team.q": "How do Team seats work?",
  "pricing.faq.team.a": "Team is billed per seat. The team shares one budget, shared connectors and shared knowledge, with owner and member roles. Every member keeps their own personal memory and conversations.",
  "pricing.faq.invoice.q": "Invoices and VAT?",
  "pricing.faq.invoice.a": "Payments go through Stripe. You get an invoice for every charge and can enter a VAT ID at checkout so it is issued to your company. VAT is worked out from your country.",
  // public landing
  "landing.headline": "A Lucy who knows you.",
  "landing.subline": "An AI companion and assistant that remembers. Talk to her on the web, on Telegram, on WhatsApp, by email or on the phone. She organises, makes pictures and video, and takes care of things for you.",
  "landing.ctaSignup": "Create an account", "landing.ctaWaitlist": "Join the waitlist", "landing.ctaSignin": "Sign in",
  "landing.haveInvite": "Have an invite?", "landing.useInvite": "Use it",
  "landing.demoTitle": "This is what a conversation looks like.",
  "landing.demo.1": "Morning. What's on today?",
  "landing.demo.2": "Morning. Two things: the 11:00 call with Maria about the site, and the dinner you wanted to book tonight. You still want the Italian place in Koukaki, right?",
  "landing.demo.3": "Yes. Book for 4 at 9 and send me a picture of you there to set the mood.",
  "landing.demo.4": "Done, table for 4 at 21:00. The picture lands on Telegram in a minute.",
  "landing.featuresTitle": "What she does",
  "landing.f1.title": "Memory and notebook",
  "landing.f1.body": "She remembers what you told her, who you mention, what gets on your nerves. Notes, documents and knowledge stay under your control.",
  "landing.f2.title": "Every channel",
  "landing.f2.body": "Web, Telegram, WhatsApp, email, phone. The same Lucy, the same memory, wherever you find her.",
  "landing.f3.title": "Pictures, video and voice",
  "landing.f3.body": "Photos and clips of the moment, voice messages in her own voice, and phone calls on your behalf.",
  "landing.pricingTitle": "Simple pricing", "landing.pricingSub": "Free to start. Pro when you want her everywhere.",
  "landing.waitlist.title": "Waitlist",
  "landing.waitlist.sub": "Lucy is invite-only right now. Leave your email and we'll send an invitation as soon as a seat opens.",
  "landing.waitlist.email": "Your email", "landing.waitlist.note": "A line about you (optional)",
  "landing.waitlist.submit": "Save me a seat", "landing.waitlist.done": "You're on the list. We'll email you.",
  "landing.waitlist.error": "Something went wrong. Try again.",
  "landing.footer.status": "System status", "landing.footer.made": "Made in Greece, hosted in the EU.",
}

const DICTS: Record<Locale, Record<string, string>> = { el, en }

function detect(): Locale {
  try {
    const stored = localStorage.getItem(KEY)
    if (stored === "el" || stored === "en") return stored
  } catch { /* private mode */ }
  const nav = (typeof navigator !== "undefined" && navigator.language) || "en"
  return nav.toLowerCase().startsWith("el") ? "el" : "en"
}

let current: Locale = detect()
const listeners = new Set<() => void>()

function apply(l: Locale) {
  current = l
  try { document.documentElement.lang = l } catch { /* ssr */ }
  listeners.forEach((fn) => fn())
}
apply(current)

export function getLocale(): Locale {
  return current
}

/** Per-browser override (also what the Settings card writes). */
export function setLocale(l: Locale) {
  try { localStorage.setItem(KEY, l) } catch { /* ignore */ }
  apply(l)
}

/** Called when /me arrives: the account preference wins over the browser. */
export function syncLocaleFromUser(ui_locale: string | null | undefined) {
  if (ui_locale === "el" || ui_locale === "en") {
    if (ui_locale !== current) setLocale(ui_locale)
  }
}

export function t(key: string, vars?: Record<string, string | number>): string {
  let s = DICTS[current][key] ?? DICTS.en[key] ?? key
  if (vars) for (const [k, v] of Object.entries(vars)) s = s.replaceAll(`{${k}}`, String(v))
  return s
}

export function useLocale(): Locale {
  return useSyncExternalStore(
    (cb) => { listeners.add(cb); return () => listeners.delete(cb) },
    () => current,
    () => current,
  )
}

/** `const t = useT()` — re-renders the component on locale change. */
export function useT() {
  useLocale()
  return t
}
